import { useEffect, useMemo, useRef, useState } from "react";
import ChatHeader from "./components/ChatHeader";
import ChatMessages from "./components/ChatMessages";
import ChatInput from "./components/ChatInput";
import SendFilePreview from "./components/SendFilePreview";
import TemplateSelectionDialog from "./components/TemplateSelectionDialog";
import type { ChatContact, ChatMessage } from "./types/chatTypes";
import { useSignalR } from "./connection/hooks/useSingleR";
import WhatsAppService from "@/services/api/whatsApp";
import { useForm } from "react-hook-form";

interface WhatsAppDrawerProps {
  LeadId: any;
  leadName?: any;
  phoneNumber?: any;
}

const WhatsAppDrawer: React.FC<WhatsAppDrawerProps> = ({
  LeadId,
  leadName,
  phoneNumber,
}) => {

  const { control } = useForm();

  const normalizedPhone = phoneNumber
    ? phoneNumber
      .toString()
      .replace(/^\+/, "")   // remove +
      .replace(/^91/, "")   // remove existing 91
      .replace(/^/, "91")   // add 91 at start
    : "";
  const initialPageSize = window.innerHeight > 900 ? 30 : 20;
  const [pageSize] = useState<number>(initialPageSize);
  const [page, setPage] = useState<number>(1);
  const { isConnected, messages: signalRMessages, clearMessages } =
    useSignalR(normalizedPhone);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [showSearch, setShowSearch] = useState(false);
  const [search, setSearch] = useState("");
  const [input, setInput] = useState("");
  const [attachOpen, setAttachOpen] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [templates, setTemplates] = useState<any[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);


  const scrollRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const shouldAutoScrollRef = useRef<boolean>(true);

  const formatISTDateTime = (value?: string) => {
    if (!value) {
      return new Date().toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    }

    // API timestamps often arrive without timezone; treat them as UTC by default.
    const raw = String(value).trim();
    const hasTimezone = /(?:Z|[+\-]\d{2}:\d{2})$/i.test(raw);
    const parsed = new Date(hasTimezone ? raw : `${raw}Z`);

    return (Number.isNaN(parsed.getTime()) ? new Date(raw) : parsed).toLocaleString(
      "en-IN",
      {
        timeZone: "Asia/Kolkata",
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }
    );
  };

  const mapHistoryMessages = (items: any[] = []): any[] =>
    items
      .filter((item: any) => {
        const text = String(item?.MessageText || item?.messageText || "").trim();
        const hasMedia = item?.MediaId || item?.mediaId || item?.MediaUrl || item?.mediaUrl;
        if (!text && !hasMedia) return false;
        // Ignore persisted webhook/status payloads that are not actual chat messages.
        return !(
          text.includes('"object":"whatsapp_business_account"') &&
          text.includes('"statuses"')
        );
      })
      .map((item: any) => {

        const rawType = String(item?.MessageType || item?.MediaType || item?.mediaType || item?.mediatype || item?.media_type || "").toLowerCase();
        let type = rawType;
        const mediaId = item?.MediaId || item?.mediaId || item?.mediaid || item?.media_id || item?.MediaID || null;
        // if (!type && mediaId) type = "image"; // guess image if has media but no type
        if (!type) type = "text";

        return {
          id: String(item.Id ?? item.id ?? Date.now()),
          text: item.MessageText || item.messageText || "",
          sender:
            String(item.Direction || item.direction || "").toLowerCase() === "outgoing"
              ? "me"
              : "them",
          time: formatISTDateTime(item.CreatedAt || item.createdAt),
          status:
            String(item.Direction || item.direction || "").toLowerCase() === "outgoing"
              ? item.Status === "Read"
                ? "read"
                : "delivered"
              : "sent",
          type: type === "document" ? "file" : type,
          mediaId: mediaId,
          fileUrl: item?.MediaUrl || item?.mediaUrl || null,
          fileName: item?.FileName || item?.fileName || null,
        };
      })
      .reverse();

  const activeContact = useMemo<any>(
    () => ({

      name: leadName || "Unknown",
      mobile: normalizedPhone,
      online: isConnected,
      messages,
    }),
    [leadName, normalizedPhone, isConnected, messages]
  );

  useEffect(() => {
    if (!normalizedPhone) return;

    clearMessages();
    setMessages([]);
    setPage(1);
    setHasMore(true);
    WhatsAppService.getWhatsAppHistory({
      phone: normalizedPhone,
      page: 1,
      pageSize,
    })
      .then((response) => {
        const apiData: any = response?.data ?? response;
        const mapped = mapHistoryMessages(apiData?.messages || []);

        setMessages(mapped);
        setHasMore(
          typeof apiData?.hasMore === "boolean"
            ? apiData.hasMore
            : (apiData?.messages?.length || 0) >= pageSize
        );

        setTimeout(() => {
          bottomRef.current?.scrollIntoView({ behavior: "smooth" });
          shouldAutoScrollRef.current = true;
        }, 100);
      })
      .catch((err) => {
        console.error("Failed to load history:", err);
      });
  }, [normalizedPhone, pageSize]);

  useEffect(() => {
    if (signalRMessages.length === 0) return;

    const latest: any = signalRMessages[signalRMessages.length - 1];
    const sender: "me" | "them" = latest.sender === "me" ? "me" : "them";
    const text = String(latest.text || "").trim();

    if (!text && !latest.mediaId && !latest.mediaUrl) return;

    shouldAutoScrollRef.current = true;
    setMessages((prev) => {
      // If backend echoes the same outgoing message, merge with optimistic one
      // instead of appending a duplicate bubble.
      if (sender === "me") {
        const optimisticIndex = [...prev]
          .reverse()
          .findIndex((message) => message.sender === "me" && message.text.trim() === text);

        if (optimisticIndex !== -1) {
          const actualIndex = prev.length - 1 - optimisticIndex;
          return prev.map((message, index) =>
            index === actualIndex
              ? {
                ...message,
                status: message.status === "read" ? "read" : "delivered",
                time: formatISTDateTime(latest.timestamp),
              }
              : message
          );
        }
      }

      const mediaId = latest.mediaId || latest.MediaId || latest.mediaid || latest.media_id || null;
      const rawMediaType = latest.mediaType || latest.MediaType || latest.mediatype || latest.media_type || "";
      let type = String(rawMediaType).toLowerCase();
      if (!type && mediaId) type = "image";
      if (!type) type = "text";

      const realtimeMessage: ChatMessage = {
        id: Date.now().toString(),
        text,
        sender,
        time: formatISTDateTime(latest.timestamp),
        status: sender === "me" ? "delivered" : "sent",
        type: type === "document" ? "file" : type,
        mediaId: mediaId,
        fileUrl: latest.mediaUrl || latest.MediaUrl || null,
        fileName: latest.fileName || latest.FileName || null,
      };

      return [...prev, realtimeMessage];
    });

    setTimeout(() => {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 50);
  }, [signalRMessages]);

  const visibleMessages = useMemo(() => {
    if (!search) return messages;
    return messages.filter((message) =>
      message.text.toLowerCase().includes(search.toLowerCase())
    );
  }, [messages, search]);

  useEffect(() => {
    if (!shouldAutoScrollRef.current) return;
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [visibleMessages]);

  const sendText = async () => {
    if (!input.trim()) return;

    const optimistic: ChatMessage = {
      id: Date.now().toString(),
      text: input.trim(),
      sender: "me",
      time: formatISTDateTime(),
      status: "sent",
      type: "text",
    };

    shouldAutoScrollRef.current = true;
    setMessages((prev) => [...prev, optimistic]);
    setInput("");

    try {
      await WhatsAppService.sendWhatsAppMessage({
        recipient: normalizedPhone,
        content: optimistic.text,
        pluginName: "whatsapp",
        mediaType: "text",
      });
    } catch (err) {
      console.error("Send failed:", err);
      setMessages((prev) =>
        prev.map((message) =>
          message.id === optimistic.id ? { ...message, status: "sent" } : message
        )
      );
    }
  };

  const sendFile = async (file: File, caption: string) => {
    if (!file) return;

    
    const fileUrl = URL.createObjectURL(file);
  
    let type: "image" | "video" | "file" = "file";
    if (file.type.startsWith("image/")) type = "image";
    else if (file.type.startsWith("video/")) type = "video";

    const optimistic: ChatMessage = {
      id: Date.now().toString(),
      text: caption,
      sender: "me",
      time: formatISTDateTime(),
      status: "sent",
      type: type,
      fileUrl: fileUrl,
      fileName: file.name,
      fileSize: (file.size / 1024 / 1024).toFixed(2) + " MB"
    };

    shouldAutoScrollRef.current = true;
    setMessages((prev) => [...prev, optimistic]);
    setSelectedFile(null); // Close the preview dialog immediately

    try {
      await WhatsAppService.sendWhatsAppMessage({
        payload: {
          recipient: normalizedPhone,
          content: caption,
          pluginName: "whatsapp",
          mediaType: type === "file" ? "document" : type,
        },
        selectedFile: file
      });
    } catch (err) {
      console.error("Send file failed:", err);
    }
  };

  const handleOpenTemplateDialog = async () => {
    setTemplateDialogOpen(true);
    if (templates.length === 0) {
      setLoadingTemplates(true);
      try {
        const response = await WhatsAppService.getTemplate();
        const data: any = response?.data ?? response;
        setTemplates(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Failed to fetch templates:", err);
      } finally {
        setLoadingTemplates(false);
      }
    }
  };

  const handleSendTemplate = async (template: any, parameters: string[]) => {
    let messageText = template.BodyFormat;
    parameters.forEach((p, i) => {
      messageText = messageText.replace(`{{${i + 1}}}`, p);
    });

    // const optimistic: ChatMessage = {
    //   id: Date.now().toString(),
    //   text: messageText,
    //   sender: "me",
    //   time: formatISTDateTime(),
    //   status: "sent",
    //   type: "text",
    // };

    // shouldAutoScrollRef.current = true;
    // setMessages((prev) => [...prev, optimistic]);

    try {
      await WhatsAppService.sendWhatsAppMessage({
        recipient: normalizedPhone,
        pluginName: "whatsapp",
        TemplateId : template.TemplateId,
        TemplateParameters : parameters,
        mediaType:"template",
        
      });
    } catch (err) {
      console.error("Send template failed:", err);
    }
  };

  const handleScroll = async () => {
    const container = scrollRef.current;
    if (!container) return;

    if (container.scrollTop === 0 && hasMore && !loadingMore) {
      shouldAutoScrollRef.current = false;
      setLoadingMore(true);

      const prevHeight = container.scrollHeight;

      try {
        const nextPage = page + 1;

        const response = await WhatsAppService.getWhatsAppHistory({
          phone: normalizedPhone,
          page: nextPage,
          pageSize,
        });

        const apiData: any = response?.data ?? response;
        const rows = apiData?.messages || [];

        if (!rows.length) {
          setHasMore(false);
          return;
        }
        const mapped = mapHistoryMessages(rows);

        if (!mapped.length) {
          setHasMore(false);
          return;
        }

        setMessages((prev) => {
          const existingIds = new Set(prev.map((msg) => msg.id));
          const olderUnique = mapped.filter((msg) => !existingIds.has(msg.id));
          return [...olderUnique, ...prev];
        });
        setPage(nextPage);
        setHasMore(
          typeof apiData?.hasMore === "boolean"
            ? apiData.hasMore
            : rows.length >= pageSize
        );

        setTimeout(() => {
          const newHeight = container.scrollHeight;
          container.scrollTop = newHeight - prevHeight;
        }, 0);
      } catch (err) {
        console.error("Pagination error:", err);
      } finally {
        setLoadingMore(false);
      }
    }
  };

  return (
    <div className="bg-transparent px-0 pt-0 pb-2 sm:px-6 md:pt-1 md:pb-3">
      <div className="h-[calc(100vh-120px)] overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-[0_8px_30px_rgb(0,0,0,0.08)]">
        <div className="flex h-full overflow-hidden flex-col relative">
          <ChatHeader
            contact={activeContact}
            search={search}
            showSearch={showSearch}
            setShowSearch={setShowSearch}
            setSearch={setSearch}
            LeadId={LeadId}
            leadName={leadName}
          />

          <div className="flex-1 overflow-hidden relative flex flex-col">
            <ChatMessages
              contact={activeContact}
              messages={visibleMessages}
              search={search}
              scrollRef={scrollRef}
              bottomRef={bottomRef}
              handleScroll={handleScroll}
              loadingMore={loadingMore}
              hasMore={hasMore}
            />

            <ChatInput
              input={input}
              setInput={setInput}
              sendText={sendText}
              attachOpen={attachOpen}
              setAttachOpen={setAttachOpen}
              control={control}
              onFileSelect={setSelectedFile}
              onTemplateClick={handleOpenTemplateDialog}
            />
            <TemplateSelectionDialog
              open={templateDialogOpen}
              onClose={() => setTemplateDialogOpen(false)}
              templates={templates}
              loading={loadingTemplates}
              onSend={handleSendTemplate}
            />
            {selectedFile && (
              <SendFilePreview
                file={selectedFile}
                onClose={() => setSelectedFile(null)}
                onSend={sendFile}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppDrawer;
